/**
 * 资产管理系统 V2
 * 核心功能：入库 → 关联 → 变更
 * 特色功能：闲置资产快速查看
 */

import React, { useState, useEffect } from 'react';
import {
  Card, Button, Table, Space, Tag, Modal, Form, Input, Select, DatePicker, InputNumber,
  message, Row, Col, Tabs, Drawer, Descriptions, Divider, Statistic, Badge, Tooltip,
  Popconfirm, Empty, Alert, Timeline, Switch
} from 'antd';
import {
  PlusOutlined, EditOutlined, EyeOutlined, DeleteOutlined, ReloadOutlined,
  LinkOutlined, InboxOutlined, WarningOutlined, UserOutlined, SwapOutlined,
  DesktopOutlined, MobileOutlined, CreditCardOutlined, MessageOutlined,
  VideoCameraOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import AttachmentUpload from '../components/AttachmentUpload';
import ImageViewer from '../components/ImageViewer';
import AssetDynamicForm from '../components/AssetDynamicForm';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

// 资产类型图标映射
const ASSET_TYPE_ICONS = {
  computer: <DesktopOutlined style={{ fontSize: 16 }} />,
  mobile_phone: <MobileOutlined style={{ fontSize: 16 }} />,
  sim_card: <CreditCardOutlined style={{ fontSize: 16 }} />,
  wechat_account: <MessageOutlined style={{ fontSize: 16 }} />,
  douyin_account: <VideoCameraOutlined style={{ fontSize: 16 }} />
};

// 资产状态映射（适配实际数据库字段）
const STATUS_MAP = {
  active: { text: '正常', color: 'green', icon: <UserOutlined /> },
  inactive: { text: '闲置', color: 'default', icon: <InboxOutlined /> },
  maintenance: { text: '维护中', color: 'blue', icon: <WarningOutlined /> },
  disposed: { text: '已报废', color: 'red' },
  lost: { text: '丢失', color: 'red' }
};

// 资产关联类型映射（仅资产之间的物理关系）
const RELATION_TYPES = {
  installed_in: { text: '安装在', color: 'blue' },
  contains: { text: '包含', color: 'cyan' },
  belongs_to: { text: '属于', color: 'purple' },
  linked_to: { text: '关联到', color: 'geekblue' }
};

const AssetManagementV2 = () => {
  // 基础状态
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [allAssets, setAllAssets] = useState([]); // 存储所有资产，不受筛选条件影响
  const [assetTypes, setAssetTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // 分页和搜索
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    type_id: undefined,
    status: undefined,
    employee_id: undefined
  });
  const [idleOnly, setIdleOnly] = useState(false); // 是否只显示闲置资产

  // 模态框状态
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [form] = Form.useForm();

  // 完善信息模态框
  const [completeInfoModalVisible, setCompleteInfoModalVisible] = useState(false);
  const [completingAsset, setCompletingAsset] = useState(null);
  const [completeForm] = Form.useForm();

  // 详情抽屉
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetRelations, setAssetRelations] = useState({ outgoing: [], incoming: [] });
  const [assignmentHistory, setAssignmentHistory] = useState([]);

  // 第二层详情抽屉（用于查看关联资产）
  const [nestedDrawerVisible, setNestedDrawerVisible] = useState(false);
  const [nestedSelectedAsset, setNestedSelectedAsset] = useState(null);
  const [nestedAssetRelations, setNestedAssetRelations] = useState({ outgoing: [], incoming: [] });
  const [nestedAssignmentHistory, setNestedAssignmentHistory] = useState([]);

  // 资产关联管理
  const [relationModalVisible, setRelationModalVisible] = useState(false);
  const [relationForm] = Form.useForm();

  // 资产领用管理
  const [assignmentModalVisible, setAssignmentModalVisible] = useState(false);
  const [assigningAsset, setAssigningAsset] = useState(null);
  const [assignmentForm] = Form.useForm();
  const [assignmentChildren, setAssignmentChildren] = useState([]); // 将要一起领用的子资产

  // 图片相关
  const [assetImageFiles, setAssetImageFiles] = useState([]);
  const assetImageUploadRef = React.useRef();
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageList, setImageList] = useState([]);

  // 动态字段相关
  const [selectedAssetTypeId, setSelectedAssetTypeId] = useState(null);
  const dynamicFormRef = React.useRef();

  // 统计数据
  const [statistics, setStatistics] = useState({
    total: 0,
    inventory: 0,
    in_use: 0,
    idle_total: 0
  });

  // 闲置资产统计
  const [idleStats, setIdleStats] = useState({
    total_idle: 0,
    by_type: [],
    by_reason: []
  });

  // 初始化
  useEffect(() => {
    fetchAssetTypes();
    fetchEmployees();
    fetchIdleStats();
    fetchAllAssets();
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [pagination.current, pagination.pageSize, searchText, filters, idleOnly]);

  // 获取资产类型
  const fetchAssetTypes = async () => {
    try {
      const response = await fetch('/api/assets/types', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setAssetTypes(data.data);
      }
    } catch (error) {
      console.error('获取资产类型失败:', error);
    }
  };

  // 获取员工列表
  const fetchEmployees = async () => {
    try {
      const response = await fetch('/api/employees?status=probation,regular,active&limit=1000', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setEmployees(data.data.employees || []);
      }
    } catch (error) {
      console.error('获取员工列表失败:', error);
    }
  };

  // 获取闲置资产统计
  const fetchIdleStats = async () => {
    try {
      const response = await fetch('/api/v2/assets/stats/idle', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setIdleStats(data.data);
      }
    } catch (error) {
      console.error('获取闲置资产统计失败:', error);
    }
  };

  // 获取所有资产（不受筛选条件影响）
  const fetchAllAssets = async () => {
    try {
      const response = await fetch('/api/v2/assets?limit=10000', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setAllAssets(data.data.assets);
      }
    } catch (error) {
      console.error('获取所有资产失败:', error);
    }
  };

  // 获取资产列表
  const fetchAssets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.current,
        limit: pagination.pageSize,
        idle_only: idleOnly ? 'true' : 'false'
      });
      
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
          params.append(key, filters[key]);
        }
      });
      
      if (searchText) {
        params.append('search', searchText);
      }

      const response = await fetch(`/api/v2/assets?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setAssets(data.data.assets);
        setPagination(prev => ({
          ...prev,
          total: data.data.pagination.total
        }));
        updateStatistics(data.data.assets);
      } else {
        message.error(data.message || '获取资产列表失败');
      }
    } catch (error) {
      console.error('获取资产列表失败:', error);
      message.error('获取资产列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 更新统计数据
  const updateStatistics = (assetsList) => {
    const stats = {
      total: assetsList.length,
      inventory: 0,
      in_use: 0,
      idle_total: 0
    };
    
    assetsList.forEach(asset => {
      if (asset.status === 'inactive') stats.inventory++;
      if (asset.status === 'active') stats.in_use++;
      // is_idle 可能是数字 0/1 或字符串 "0"/"1"
      if (asset.is_idle === 1 || asset.is_idle === '1' || asset.is_idle === true) stats.idle_total++;
    });
    
    setStatistics(stats);
  };

  // 打开创建/编辑模态框
  const openModal = (asset = null) => {
    setEditingAsset(asset);
    setModalVisible(true);
    
    if (asset) {
      // 编辑模式
      setSelectedAssetTypeId(asset.asset_type_id);
      
      if (asset.image_url) {
        try {
          const imageUrls = typeof asset.image_url === 'string' && asset.image_url.startsWith('[')
            ? JSON.parse(asset.image_url)
            : [asset.image_url];
          
          const imageFiles = imageUrls.map((url, index) => ({
            uid: `image-${asset.id}-${index}`,
            name: `资产图片-${index + 1}`,
            status: 'done',
            url: url,
            thumbUrl: url,
            type: 'image/jpeg',
            fileData: { path: url }
          }));
          setAssetImageFiles(imageFiles);
        } catch (error) {
          setAssetImageFiles([]);
        }
      }
      
      form.setFieldsValue({
        ...asset,
        purchase_date: asset.purchase_date ? dayjs(asset.purchase_date) : null,
        warranty_expires: asset.warranty_expires ? dayjs(asset.warranty_expires) : null
      });
    } else {
      setAssetImageFiles([]);
      setSelectedAssetTypeId(null);
      form.resetFields();
    }
  };

  // 关闭模态框
  const closeModal = () => {
    setModalVisible(false);
    setEditingAsset(null);
    setAssetImageFiles([]);
    setSelectedAssetTypeId(null);
    form.resetFields();
  };

  // 提交表单（快速入库）
  const handleSubmit = async (values) => {
    try {
      const submitData = {
        ...values,
        status: 'inactive' // 新入库的资产默认为闲置状态
      };

      // 清理空值字段
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === null || submitData[key] === undefined || submitData[key] === '') {
          delete submitData[key];
        }
      });

      const response = await fetch('/api/v2/assets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (data.success) {
        message.success('资产入库成功！您可以在列表中找到它，点击"完善信息"按钮继续配置。');
        closeModal();
        fetchAssets();
        fetchIdleStats();
        
        // 提示用户完善信息
        Modal.info({
          title: '资产入库成功',
          content: (
            <div>
              <p>资产编号：{data.data.asset_no}</p>
              <p>下一步：点击资产列表中的"编辑"按钮，完善详细信息和自定义字段。</p>
            </div>
          ),
          okText: '知道了'
        });
      } else {
        message.error(data.message || '操作失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
      message.error('操作失败');
    }
  };

  // 打开完善信息模态框
  const openCompleteInfoModal = async (asset) => {
    setCompletingAsset(asset);
    setSelectedAssetTypeId(asset.asset_type_id);
    setCompleteInfoModalVisible(true);
    
    // 获取完整的资产详情（包括动态字段值）
    try {
      const response = await fetch(`/api/v2/assets/${asset.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      
      if (data.success) {
        const fullAsset = data.data;
        
        // 设置图片
        if (fullAsset.image_url) {
          try {
            const imageUrls = typeof fullAsset.image_url === 'string' && fullAsset.image_url.startsWith('[')
              ? JSON.parse(fullAsset.image_url)
              : [fullAsset.image_url];
            
            const imageFiles = imageUrls.map((url, index) => ({
              uid: `image-${fullAsset.id}-${index}`,
              name: `资产图片-${index + 1}`,
              status: 'done',
              url: url,
              thumbUrl: url,
              type: 'image/jpeg',
              fileData: { path: url }
            }));
            setAssetImageFiles(imageFiles);
          } catch (error) {
            setAssetImageFiles([]);
          }
        } else {
          setAssetImageFiles([]);
        }
        
        // 设置表单值（包括动态字段）
        completeForm.setFieldsValue({
          ...fullAsset,
          purchase_date: fullAsset.purchase_date ? dayjs(fullAsset.purchase_date) : null,
          warranty_expires: fullAsset.warranty_expires ? dayjs(fullAsset.warranty_expires) : null
        });
      }
    } catch (error) {
      console.error('获取资产详情失败:', error);
      // 如果获取失败，使用传入的asset数据
      if (asset.image_url) {
        try {
          const imageUrls = typeof asset.image_url === 'string' && asset.image_url.startsWith('[')
            ? JSON.parse(asset.image_url)
            : [asset.image_url];
          
          const imageFiles = imageUrls.map((url, index) => ({
            uid: `image-${asset.id}-${index}`,
            name: `资产图片-${index + 1}`,
            status: 'done',
            url: url,
            thumbUrl: url,
            type: 'image/jpeg',
            fileData: { path: url }
          }));
          setAssetImageFiles(imageFiles);
        } catch (error) {
          setAssetImageFiles([]);
        }
      } else {
        setAssetImageFiles([]);
      }
      
      completeForm.setFieldsValue({
        ...asset,
        purchase_date: asset.purchase_date ? dayjs(asset.purchase_date) : null,
        warranty_expires: asset.warranty_expires ? dayjs(asset.warranty_expires) : null
      });
    }
  };

  // 提交完善信息
  const handleCompleteInfoSubmit = async (values) => {
    try {
      const submitData = {
        ...values,
        purchase_date: values.purchase_date ? values.purchase_date.format('YYYY-MM-DD') : null,
        warranty_expires: values.warranty_expires ? values.warranty_expires.format('YYYY-MM-DD') : null
      };

      // 移除所有以field_开头的字段（这些会通过dynamic_fields发送）
      Object.keys(submitData).forEach(key => {
        if (key.startsWith('field_')) {
          delete submitData[key];
        }
      });

      // 清理空值字段（但保留dynamic_fields）
      Object.keys(submitData).forEach(key => {
        if (key !== 'dynamic_fields' && (submitData[key] === null || submitData[key] === undefined || submitData[key] === '')) {
          delete submitData[key];
        }
      });

      // 处理动态字段数据
      if (dynamicFormRef.current && selectedAssetTypeId) {
        const processedFields = dynamicFormRef.current.processFormData(values);
        if (processedFields && processedFields.length > 0) {
          submitData.dynamic_fields = processedFields;
        }
      }

      const response = await fetch(`/api/v2/assets/${completingAsset.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (data.success) {
        message.success('资产信息完善成功！');
        setCompleteInfoModalVisible(false);
        setCompletingAsset(null);
        setAssetImageFiles([]);
        setSelectedAssetTypeId(null);
        completeForm.resetFields();
        fetchAssets();
        fetchIdleStats();
      } else {
        message.error(data.message || '操作失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
      message.error('操作失败');
    }
  };

  // 查看资产详情
  const viewAssetDetails = async (asset) => {
    setDrawerVisible(true);
    
    // 获取完整的资产详情（包括动态字段值）
    try {
      const response = await fetch(`/api/v2/assets/${asset.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setSelectedAsset(data.data); // 使用完整的资产数据
      } else {
        setSelectedAsset(asset); // 降级使用列表数据
      }
    } catch (error) {
      console.error('获取资产详情失败:', error);
      setSelectedAsset(asset); // 降级使用列表数据
    }
    
    // 获取关系信息（包括历史记录）
    try {
      const response = await fetch(`/api/v2/asset-relations/asset/${asset.id}?status=all`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setAssetRelations(data.data);
      }
    } catch (error) {
      console.error('获取关系信息失败:', error);
    }

    // 获取领用历史
    try {
      const historyResponse = await fetch(`/api/assets/${asset.id}/history?action_type=assigned,returned`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const historyData = await historyResponse.json();
      if (historyData.success) {
        setAssignmentHistory(historyData.data || []);
      }
    } catch (error) {
      console.error('获取领用历史失败:', error);
    }
  };

  // 查看嵌套资产详情（在资产关联中点击时使用）
  const viewNestedAssetDetails = async (asset) => {
    setNestedDrawerVisible(true);
    
    // 获取完整的资产详情（包括动态字段值）
    try {
      const response = await fetch(`/api/v2/assets/${asset.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setNestedSelectedAsset(data.data); // 使用完整的资产数据
      } else {
        setNestedSelectedAsset(asset); // 降级使用列表数据
      }
    } catch (error) {
      console.error('获取资产详情失败:', error);
      setNestedSelectedAsset(asset); // 降级使用列表数据
    }
    
    // 获取关系信息（包括历史记录）
    try {
      const response = await fetch(`/api/v2/asset-relations/asset/${asset.id}?status=all`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setNestedAssetRelations(data.data);
      }
    } catch (error) {
      console.error('获取关系信息失败:', error);
    }

    // 获取领用历史
    try {
      const historyResponse = await fetch(`/api/assets/${asset.id}/history?action_type=assigned,returned`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const historyData = await historyResponse.json();
      if (historyData.success) {
        setNestedAssignmentHistory(historyData.data || []);
      }
    } catch (error) {
      console.error('获取领用历史失败:', error);
    }
  };

  // 删除资产
  const handleDelete = async (id) => {
    try {
      const response = await fetch(`/api/v2/assets/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const data = await response.json();

      if (data.success) {
        message.success('资产删除成功');
        fetchAssets();
        fetchIdleStats();
      } else {
        message.error(data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  // 打开资产关联模态框
  const openRelationModal = (asset) => {
    setSelectedAsset(asset);
    setRelationModalVisible(true);
    relationForm.resetFields();
  };

  // 打开资产领用模态框
  const openAssignmentModal = async (asset) => {
    // 检查资产信息是否完善
    const hasBasicInfo = asset.purchase_price || asset.supplier || asset.image_url;
    const hasCustomFields = asset._field_values && asset._field_values.length > 0;
    
    if (!hasBasicInfo && !hasCustomFields) {
      Modal.confirm({
        title: '资产信息未完善',
        content: '建议先完善资产的详细信息（购买价格、供应商、图片、自定义字段等）后再进行领用操作。是否继续？',
        okText: '完善信息',
        cancelText: '仍然领用',
        onOk: () => {
          openCompleteInfoModal(asset);
        },
        onCancel: async () => {
          // 用户选择继续领用，查询子资产
          await checkAndShowAssignmentModal(asset);
        }
      });
      return;
    }
    
    // 查询并验证子资产
    await checkAndShowAssignmentModal(asset);
  };

  // 检查子资产并显示领用模态框（支持领用子资产时的父资产联动）
  const checkAndShowAssignmentModal = async (asset) => {
    try {
      // 查询该资产的所有关系（作为父资产和子资产）
      const relResponse = await fetch(`/api/v2/asset-relations/asset/${asset.id}?status=active`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const relData = await relResponse.json();
      
      console.log('🔍 检查资产关系:', {
        assetId: asset.id,
        assetName: asset.name,
        relData: relData.data
      });
      
      if (relData.success) {
        // 1. 检查该资产是否是子资产
        // installed_in/belongs_to: 当前资产是子资产，关系在outgoing中，target_id是父资产
        // contains: 当前资产是子资产，关系在incoming中，source_id是父资产
        let parentRelation = null;
        let parentAssetId = null;
        
        // 检查出向关系（installed_in, belongs_to）
        const outgoingParentRel = (relData.data.outgoing || []).find(rel => 
          rel.relation_name === 'installed_in' || 
          rel.relation_name === 'belongs_to'
        );
        if (outgoingParentRel) {
          parentRelation = outgoingParentRel;
          parentAssetId = outgoingParentRel.target_id; // 父资产ID
          console.log('✅ 发现父资产（outgoing）:', {
            relation: outgoingParentRel.relation_name,
            parentAssetId
          });
        }
        
        // 检查入向关系（contains）
        if (!parentRelation) {
          const incomingParentRel = (relData.data.incoming || []).find(rel => 
            rel.relation_name === 'contains'
          );
          if (incomingParentRel) {
            parentRelation = incomingParentRel;
            parentAssetId = incomingParentRel.source_id; // 父资产ID
            console.log('✅ 发现父资产（incoming）:', {
              relation: incomingParentRel.relation_name,
              parentAssetId
            });
          }
        }

        if (parentRelation && parentAssetId) {
          // 该资产是子资产，查找父资产信息
          const parentAsset = assets.find(a => a.id === parentAssetId);
          console.log('🔍 查找父资产结果:', {
            parentAssetId,
            found: !!parentAsset,
            parentAsset: parentAsset ? { id: parentAsset.id, name: parentAsset.name } : null,
            assetsCount: assets.length
          });
          
          if (parentAsset) {
            // 检查父资产的领用状态
            if (parentAsset.current_holder) {
              // 情况1：父资产已被其他人领用
              if (parentAsset.current_holder !== asset.current_holder) {
                Modal.error({
                  title: '无法领用',
                  content: (
                    <div>
                      <p>该资产属于 <strong>{parentAsset.name}</strong>，而该父资产已被 <strong>{parentAsset.holder_name}</strong> 领用。</p>
                      <p>无法单独领用子资产。</p>
                    </div>
                  ),
                  okText: '知道了'
                });
                return;
              }
              // 情况2：父资产已被自己领用，直接领用子资产（补充领用）
              setAssignmentChildren([]);
              setAssigningAsset(asset);
              setAssignmentModalVisible(true);
              assignmentForm.resetFields();
              return;
            } else {
              // 情况3：父资产闲置，询问是否领用整套
              // 查询父资产的所有子资产
              const parentRelResponse = await fetch(`/api/v2/asset-relations/asset/${parentAsset.id}?status=active`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
              });
              const parentRelData = await parentRelResponse.json();
              
              const allChildrenOfParent = [];
              if (parentRelData.success) {
                for (const rel of parentRelData.data.outgoing || []) {
                  const childAsset = assets.find(a => a.id === rel.child_asset_id);
                  if (childAsset) {
                    allChildrenOfParent.push({
                      ...childAsset,
                      relation_type: rel.relation_name
                    });
                  }
                }
              }

              // 检查是否有其他子资产被占用
              const occupiedSiblings = allChildrenOfParent.filter(child => 
                child.current_holder && child.id !== asset.id
              );

              if (occupiedSiblings.length > 0) {
                Modal.error({
                  title: '无法领用整套',
                  content: (
                    <div>
                      <p>以下关联资产已被其他人领用：</p>
                      <ul>
                        {occupiedSiblings.map((child, index) => (
                          <li key={index}>
                            <strong>{child.name}</strong> (当前持有人：{child.holder_name})
                          </li>
                        ))}
                      </ul>
                      <p>只能单独领用 <strong>{asset.name}</strong>，或先归还以上资产后领用整套。</p>
                    </div>
                  ),
                  okText: '只领用当前资产',
                  onOk: () => {
                    setAssignmentChildren([]);
                    setAssigningAsset(asset);
                    setAssignmentModalVisible(true);
                    assignmentForm.resetFields();
                  }
                });
                return;
              }

              // 询问是否领用整套
              Modal.confirm({
                title: '领用整套资产',
                content: (
                  <div>
                    <p><strong>{asset.name}</strong> 属于 <strong>{parentAsset.name}</strong></p>
                    <p>是否领用整套资产？</p>
                    <div style={{ marginTop: 16 }}>
                      <p><strong>将领用以下资产：</strong></p>
                      <ul style={{ listStyle: 'none', padding: 0 }}>
                        <li style={{ marginBottom: 8 }}>
                          📦 <strong>{parentAsset.name}</strong> 
                          <Tag color="blue" style={{ marginLeft: 8 }}>{parentAsset.type_name}</Tag>
                          <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>({parentAsset.asset_no})</span>
                          <Tag color="purple" style={{ marginLeft: 8 }}>父资产</Tag>
                        </li>
                        {allChildrenOfParent.map((child, index) => (
                          <li key={index} style={{ marginBottom: 8 }}>
                            📌 {child.name}
                            <Tag color="cyan" style={{ marginLeft: 8 }}>{child.type_name}</Tag>
                            <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>({child.asset_no})</span>
                            {child.id === asset.id && <Tag color="blue" style={{ marginLeft: 8 }}>当前选择</Tag>}
                          </li>
                        ))}
                      </ul>
                      <p>共 {1 + allChildrenOfParent.length} 个资产</p>
                    </div>
                  </div>
                ),
                okText: '领用整套',
                cancelText: '只领用当前资产',
                width: 600,
                onOk: () => {
                  // 领用整套：父资产 + 所有子资产
                  setAssignmentChildren(allChildrenOfParent);
                  setAssigningAsset(parentAsset); // 主资产改为父资产
                  setAssignmentModalVisible(true);
                  assignmentForm.resetFields();
                },
                onCancel: () => {
                  // 只领用当前子资产
                  setAssignmentChildren([]);
                  setAssigningAsset(asset);
                  setAssignmentModalVisible(true);
                  assignmentForm.resetFields();
                }
              });
              return;
            }
          }
        }

        // 2. 该资产是父资产或独立资产，检查其子资产
        const childAssets = [];
        const occupiedChildren = [];
        
        // 只查询出向关系（该资产作为父资产）
        for (const rel of relData.data.outgoing || []) {
          // 查找子资产的详细信息
          const childAsset = assets.find(a => a.id === rel.child_asset_id);
          if (childAsset) {
            childAssets.push({
              ...childAsset,
              relation_type: rel.relation_name
            });
            
            // 检查子资产是否已被其他人领用
            if (childAsset.current_holder && childAsset.current_holder !== asset.current_holder) {
              occupiedChildren.push({
                name: childAsset.name,
                holder: childAsset.holder_name
              });
            }
          }
        }
        
        // 如果有子资产被其他人领用，阻止操作
        if (occupiedChildren.length > 0) {
          Modal.error({
            title: '无法领用',
            content: (
              <div>
                <p>以下关联资产已被其他人领用，请先归还后再操作：</p>
                <ul>
                  {occupiedChildren.map((child, index) => (
                    <li key={index}>
                      <strong>{child.name}</strong> (当前持有人：{child.holder})
                    </li>
                  ))}
                </ul>
              </div>
            ),
            okText: '知道了'
          });
          return;
        }
        
        // 如果有子资产，询问是否一起领用
        if (childAssets.length > 0) {
          Modal.confirm({
            title: '领用整套资产',
            content: (
              <div>
                <p><strong>{asset.name}</strong> 有 {childAssets.length} 个关联资产</p>
                <p>是否领用整套资产？</p>
                <div style={{ marginTop: 16 }}>
                  <p><strong>将领用以下资产：</strong></p>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    <li style={{ marginBottom: 8 }}>
                      📦 <strong>{asset.name}</strong>
                      <Tag color="blue" style={{ marginLeft: 8 }}>{asset.type_name}</Tag>
                      <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>({asset.asset_no})</span>
                      <Tag color="purple" style={{ marginLeft: 8 }}>父资产</Tag>
                    </li>
                    {childAssets.map((child, index) => (
                      <li key={index} style={{ marginBottom: 8 }}>
                        📌 {child.name}
                        <Tag color="cyan" style={{ marginLeft: 8 }}>{child.type_name}</Tag>
                        <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>({child.asset_no})</span>
                      </li>
                    ))}
                  </ul>
                  <p>共 {1 + childAssets.length} 个资产</p>
                </div>
              </div>
            ),
            okText: '领用整套',
            cancelText: '只领用父资产',
            width: 600,
            onOk: () => {
              // 领用整套：父资产 + 所有子资产
              setAssignmentChildren(childAssets);
              setAssigningAsset(asset);
              setAssignmentModalVisible(true);
              assignmentForm.resetFields();
            },
            onCancel: () => {
              // 只领用父资产
              setAssignmentChildren([]);
              setAssigningAsset(asset);
              setAssignmentModalVisible(true);
              assignmentForm.resetFields();
            }
          });
          return;
        }
        
        // 没有子资产，直接打开模态框
        setAssignmentChildren([]);
        setAssigningAsset(asset);
        setAssignmentModalVisible(true);
        assignmentForm.resetFields();
      }
    } catch (error) {
      console.error('查询子资产失败:', error);
      // 如果查询失败，仍然允许领用（但只领用主资产）
      setAssignmentChildren([]);
      setAssigningAsset(asset);
      setAssignmentModalVisible(true);
      assignmentForm.resetFields();
    }
  };

  // 创建资产关联（包括自动传导领用）
  const handleCreateRelation = async (values) => {
    try {
      // 先确定父子关系
      let parentAssetId, childAssetId, parentAsset, childAsset;
      
      if (values.relation_name === 'belongs_to' || values.relation_name === 'installed_in') {
        // selectedAsset是子资产，target是父资产
        childAssetId = selectedAsset.id;
        parentAssetId = values.target_id;
        childAsset = selectedAsset;
        parentAsset = allAssets.find(a => a.id === values.target_id);
      } else if (values.relation_name === 'contains') {
        // selectedAsset是父资产，target是子资产
        parentAssetId = selectedAsset.id;
        childAssetId = values.target_id;
        parentAsset = selectedAsset;
        childAsset = allAssets.find(a => a.id === values.target_id);
      } else {
        // linked_to: 不涉及父子关系，不传导领用
        parentAsset = null;
        childAsset = null;
      }

      // 如果有父子关系，检查冲突
      if (parentAsset && childAsset) {
        // 检查：父资产已领用，子资产也已被其他人领用
        if (parentAsset.current_holder && childAsset.current_holder) {
          if (parentAsset.current_holder !== childAsset.current_holder) {
            Modal.error({
              title: '无法创建关联',
              content: `${parentAsset.name}已被${parentAsset.holder_name}领用，而${childAsset.name}已被${childAsset.holder_name}领用。请先归还其中一个资产后再操作。`,
              okText: '知道了'
            });
            return;
          }
        }
      }

      const submitData = {
        relation_category: 'asset_to_asset',
        source_asset_id: selectedAsset.id,
        target_type: 'asset',
        target_id: values.target_id,
        relation_name: values.relation_name,
        notes: values.notes
      };

      const response = await fetch('/api/v2/asset-relations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (data.success) {
        // 如果父资产已被领用，检查是否需要自动分配子资产
        if (parentAsset && childAsset && parentAsset.current_holder && !childAsset.current_holder) {
          const confirm = await new Promise((resolve) => {
            Modal.confirm({
              title: '自动分配子资产',
              content: (
                <div>
                  <p><strong>{parentAsset.name}</strong> 已被 <strong>{parentAsset.holder_name}</strong> 领用</p>
                  <p>是否将 <strong>{childAsset.name}</strong> 也自动分配给该员工？</p>
                </div>
              ),
              okText: '自动分配',
              cancelText: '暂不分配',
              onOk: () => resolve(true),
              onCancel: () => resolve(false)
            });
          });

          if (confirm) {
            // 自动分配子资产
            await fetch(`/api/v2/assets/${childAsset.id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify({ 
                status: 'active',
                current_holder: parentAsset.current_holder,
                action_type: 'assigned',
                old_value: JSON.stringify({ 
                  current_holder: null,
                  holder_name: null,
                  status: childAsset.status
                }),
                new_value: JSON.stringify({ 
                  current_holder: parentAsset.current_holder,
                  holder_name: parentAsset.holder_name,
                  status: 'active'
                }),
                description: `随主资产${parentAsset.name}自动分配给${parentAsset.holder_name}`
              })
            });
            message.success('关联成功，子资产已自动分配');
          } else {
            message.success('资产关联成功');
          }
        } else {
          // 如果创建的是归属关系但没有领用传导，仅更新状态
          if ((values.relation_name === 'belongs_to' || values.relation_name === 'installed_in') && childAsset && !childAsset.current_holder) {
            await fetch(`/api/v2/assets/${childAsset.id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify({ status: 'active' })
            });
          }
          message.success('资产关联成功');
        }

        setRelationModalVisible(false);
        relationForm.resetFields();
        fetchAssets();
        fetchIdleStats();
        
        // 刷新详情抽屉的关系信息（包括历史记录）
        if (drawerVisible && selectedAsset) {
          try {
            const relResponse = await fetch(`/api/v2/asset-relations/asset/${selectedAsset.id}?status=all`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const relData = await relResponse.json();
            if (relData.success) {
              setAssetRelations(relData.data);
            }
          } catch (error) {
            console.error('刷新关系信息失败:', error);
          }
        }
      } else {
        message.error(data.message || '关联失败');
      }
    } catch (error) {
      console.error('创建关联失败:', error);
      message.error('创建关联失败');
    }
  };

  // 归还资产（包括子资产联动）
  const handleReturnAsset = async (asset) => {
    try {
      // 查询该资产的所有子资产
      const relResponse = await fetch(`/api/v2/asset-relations/asset/${asset.id}?status=active`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const relData = await relResponse.json();
      
      const childAssetsToReturn = [];
      if (relData.success && relData.data.outgoing) {
        // 查找所有子资产（出向关系）
        for (const rel of relData.data.outgoing) {
          const childAsset = assets.find(a => a.id === rel.child_asset_id);
          if (childAsset && childAsset.current_holder === asset.current_holder) {
            childAssetsToReturn.push(childAsset);
          }
        }
      }

      // 如果有子资产，显示确认对话框
      if (childAssetsToReturn.length > 0) {
        const confirm = await new Promise((resolve) => {
          Modal.confirm({
            title: '归还资产及关联资产',
            content: (
              <div>
                <p>将同时归还以下资产：</p>
                <ul>
                  <li><strong>{asset.name}</strong> (核心资产)</li>
                  {childAssetsToReturn.map(child => (
                    <li key={child.id}>{child.name} ({child.asset_no})</li>
                  ))}
                </ul>
                <p style={{ marginTop: 12 }}>共 {1 + childAssetsToReturn.length} 个资产</p>
              </div>
            ),
            okText: '全部归还',
            cancelText: '只归还核心资产',
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });

        if (!confirm) {
          // 用户选择只归还核心资产
          childAssetsToReturn.length = 0;
        }
      }

      // 记录归还前的持有人信息
      const oldHolder = asset.current_holder;
      const oldHolderName = asset.holder_name || '未知员工';

      // 准备批量归还的资产列表
      const assetsToReturn = [
        { id: asset.id, name: asset.name, oldHolder, oldHolderName, oldStatus: asset.status }
      ];
      childAssetsToReturn.forEach(child => {
        assetsToReturn.push({
          id: child.id,
          name: child.name,
          oldHolder: child.current_holder,
          oldHolderName: child.holder_name || oldHolderName,
          oldStatus: child.status
        });
      });

      // 批量归还所有资产
      const returnPromises = assetsToReturn.map(async (assetItem, index) => {
        const isMainAsset = index === 0;
        const description = isMainAsset 
          ? `${assetItem.oldHolderName} 归还资产`
          : `${assetItem.oldHolderName} 归还资产（随主资产联动归还）`;

        return fetch(`/api/v2/assets/${assetItem.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ 
            status: 'inactive',
            current_holder: null,
            action_type: 'returned',
            old_value: JSON.stringify({ 
              current_holder: assetItem.oldHolder,
              holder_name: assetItem.oldHolderName,
              status: assetItem.oldStatus
            }),
            new_value: JSON.stringify({ 
              current_holder: null,
              holder_name: null,
              status: 'inactive'
            }),
            description
          })
        }).then(res => res.json());
      });

      const results = await Promise.all(returnPromises);

      // 检查是否所有归还都成功
      const allSuccess = results.every(r => r.success);

      if (allSuccess) {
        message.success(
          childAssetsToReturn.length > 0 
            ? `成功归还 ${assetsToReturn.length} 个资产（1个核心资产 + ${childAssetsToReturn.length}个关联资产）` 
            : '资产归还成功'
        );
        await fetchAssets();
        await fetchIdleStats();
        
        // 刷新详情抽屉和领用历史
        if (drawerVisible && selectedAsset) {
          try {
            const assetResponse = await fetch(`/api/v2/assets/${asset.id}`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const assetData = await assetResponse.json();
            if (assetData.success) {
              setSelectedAsset(assetData.data);
            }

            // 获取领用历史
            const historyResponse = await fetch(`/api/assets/${asset.id}/history?action_type=assigned,returned`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const historyData = await historyResponse.json();
            if (historyData.success) {
              setAssignmentHistory(historyData.data || []);
            }
          } catch (error) {
            console.error('刷新资产信息失败:', error);
          }
        }
      } else {
        const failedAssets = results
          .map((r, i) => ({ result: r, asset: assetsToReturn[i] }))
          .filter(item => !item.result.success)
          .map(item => item.asset.name)
          .join('、');
        message.error(`部分资产归还失败：${failedAssets}`);
      }
    } catch (error) {
      console.error('归还失败:', error);
      message.error('归还失败');
    }
  };

  // 资产领用（包括子资产联动）
  const handleAssignment = async (values) => {
    try {
      // 检查资产是否已被领用
      if (assigningAsset.current_holder) {
        const confirm = await new Promise((resolve) => {
          Modal.confirm({
            title: '资产已被领用',
            content: `该资产当前由其他员工持有，是否要转移领用？`,
            okText: '转移领用',
            cancelText: '取消',
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
        
        if (!confirm) {
          return;
        }
      }

      // 获取员工名称
      const selectedEmployee = employees.find(e => e.id === values.employee_id);
      const employeeName = selectedEmployee?.name || '未知员工';

      // 准备批量更新的资产列表（主资产 + 子资产）
      const assetsToUpdate = [
        { 
          id: assigningAsset.id, 
          name: assigningAsset.name,
          oldHolder: assigningAsset.current_holder,
          oldHolderName: assigningAsset.holder_name || null,
          oldStatus: assigningAsset.status
        }
      ];

      // 添加所有子资产
      assignmentChildren.forEach(child => {
        assetsToUpdate.push({
          id: child.id,
          name: child.name,
          oldHolder: child.current_holder,
          oldHolderName: child.holder_name || null,
          oldStatus: child.status
        });
      });

      // 批量更新所有资产
      const updatePromises = assetsToUpdate.map(async (asset, index) => {
        const isMainAsset = index === 0;
        const description = isMainAsset 
          ? `${employeeName} 领用资产${values.notes ? '，备注：' + values.notes : ''}`
          : `${employeeName} 领用资产（随主资产自动分配）`;

        return fetch(`/api/v2/assets/${asset.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ 
            status: 'active',
            current_holder: values.employee_id,
            action_type: 'assigned',
            old_value: JSON.stringify({ 
              current_holder: asset.oldHolder || null,
              holder_name: asset.oldHolderName,
              status: asset.oldStatus
            }),
            new_value: JSON.stringify({ 
              current_holder: values.employee_id,
              holder_name: employeeName,
              status: 'active'
            }),
            description
          })
        }).then(res => res.json());
      });

      const results = await Promise.all(updatePromises);

      // 检查是否所有更新都成功
      const allSuccess = results.every(r => r.success);
      const failedAssets = results
        .map((r, i) => ({ result: r, asset: assetsToUpdate[i] }))
        .filter(item => !item.result.success);

      if (allSuccess) {
        message.success(
          assignmentChildren.length > 0 
            ? `成功领用 ${assetsToUpdate.length} 个资产（1个核心资产 + ${assignmentChildren.length}个关联资产）` 
            : '资产领用成功'
        );
        setAssignmentModalVisible(false);
        setAssigningAsset(null);
        setAssignmentChildren([]);
        assignmentForm.resetFields();
        await fetchAssets();
        await fetchIdleStats();
        
        // 如果详情抽屉打开，刷新资产信息和领用历史
        if (drawerVisible && assigningAsset) {
          try {
            const assetResponse = await fetch(`/api/v2/assets/${assigningAsset.id}`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const assetData = await assetResponse.json();
            if (assetData.success) {
              setSelectedAsset(assetData.data);
            }

            // 获取领用历史
            const historyResponse = await fetch(`/api/assets/${assigningAsset.id}/history?action_type=assigned,returned`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const historyData = await historyResponse.json();
            if (historyData.success) {
              setAssignmentHistory(historyData.data || []);
            }
          } catch (error) {
            console.error('刷新资产信息失败:', error);
          }
        }
      } else {
        // 部分失败
        const failedNames = failedAssets.map(item => item.asset.name).join('、');
        message.error(`部分资产领用失败：${failedNames}`);
      }
      
    } catch (error) {
      console.error('领用失败:', error);
      message.error('领用失败');
    }
  };

  // 解除关系
  const handleDeleteRelation = async (relationId, reason) => {
    try {
      const response = await fetch(`/api/v2/asset-relations/${relationId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ reason })
      });

      const data = await response.json();

      if (data.success) {
        message.success('关系已解除');
        
        // 立即刷新列表数据（确保按钮状态更新）
        await fetchAssets();
        await fetchIdleStats();
        
        // 刷新详情抽屉的关系信息（包括历史记录）
        if (drawerVisible && selectedAsset) {
          try {
            const relResponse = await fetch(`/api/v2/asset-relations/asset/${selectedAsset.id}?status=all`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const relData = await relResponse.json();
            if (relData.success) {
              setAssetRelations(relData.data);
            }
          } catch (error) {
            console.error('刷新关系信息失败:', error);
          }
        }
      } else {
        message.error(data.message || '解除失败');
      }
    } catch (error) {
      console.error('解除关系失败:', error);
      message.error('解除关系失败');
    }
  };

  // 图片预览
  const openImageViewer = (images, index = 0) => {
    setImageList(images);
    setCurrentImageIndex(index);
    setImageViewerVisible(true);
  };

  const closeImageViewer = () => {
    setImageViewerVisible(false);
    setImageList([]);
    setCurrentImageIndex(0);
  };

  // 表格列定义
  const columns = [
    {
      title: '资产编号',
      dataIndex: 'asset_no',
      key: 'asset_no',
      width: 150,
      fixed: 'left'
    },
    {
      title: '资产名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
      render: (text, record) => {
        const isIncomplete = !record.purchase_price && !record.supplier && !record.image_url;
        // is_idle 可能是数字 0/1 或字符串 "0"/"1"
        const isIdle = record.is_idle === 1 || record.is_idle === '1' || record.is_idle === true;
        return (
          <Space>
            {ASSET_TYPE_ICONS[record.type_code]}
            <span>{text}</span>
            {isIdle && (
              <Badge status="warning" text="闲置" />
            )}
            {isIncomplete && (
              <Tooltip title="信息未完善">
                <Badge status="processing" text="待完善" />
              </Tooltip>
            )}
          </Space>
        );
      }
    },
    {
      title: '类型',
      dataIndex: 'type_name',
      key: 'type_name',
      width: 120,
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, record) => {
        // 优先根据持有人判断状态
        let displayStatus = status;
        if (record.current_holder) {
          // 如果有持有人，应该显示为使用中
          displayStatus = 'active';
        } else if (!record.current_holder && status === 'active') {
          // 如果没有持有人但状态是active，显示为闲置
          displayStatus = 'inactive';
        }
        
        const config = STATUS_MAP[displayStatus] || { text: displayStatus, color: 'default' };
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      }
    },
    {
      title: '位置',
      dataIndex: 'location',
      key: 'location',
      width: 120,
      ellipsis: true
    },
    {
      title: '购买价格',
      dataIndex: 'purchase_price',
      key: 'purchase_price',
      width: 100,
      render: (price) => price ? `¥${price}` : '-'
    },
    {
      title: '入库时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right',
      align: 'right',
      render: (_, record) => {
        // 判断是否已有归属关系（检查是否作为子资产存在于关系中）
        // MySQL返回的是字符串"0"或"1"，需要转换为布尔值
        const hasParent = record.has_parent_relation === 1 || record.has_parent_relation === '1';
        // 判断是否已被领用
        const isAssigned = record.current_holder;

        return (
          <Space size="small">
            <Tooltip title="查看详情">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => viewAssetDetails(record)}
              />
            </Tooltip>
            <Tooltip title="完善信息">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => openCompleteInfoModal(record)}
              />
            </Tooltip>
            <Tooltip title={hasParent ? "该资产已归属于其他资产" : "建立资产关联"}>
              <Button
                type="text"
                icon={<LinkOutlined />}
                onClick={() => openRelationModal(record)}
                disabled={hasParent}
                style={hasParent ? { opacity: 0.4 } : {}}
              />
            </Tooltip>
            <Tooltip title={isAssigned ? `已领用（当前持有人：${record.holder_name || '未知'}）` : "分配给员工"}>
              <Button
                type="text"
                icon={<UserOutlined />}
                onClick={() => openAssignmentModal(record)}
                disabled={isAssigned}
                style={isAssigned ? { opacity: 0.4 } : {}}
              />
            </Tooltip>
            <Popconfirm
              title="确认删除此资产？"
              description="删除后无法恢复"
              onConfirm={() => handleDelete(record.id)}
              okText="删除"
              cancelText="取消"
            >
              <Tooltip title="删除">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  return (
    <div style={{ padding: '10px 16px' }}>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title="总资产" 
              value={pagination.total} 
              prefix={<DesktopOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="库存中" 
              value={statistics.inventory} 
              valueStyle={{ color: '#999' }}
              prefix={<InboxOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="使用中" 
              value={statistics.in_use} 
              valueStyle={{ color: '#3f8600' }}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card 
            style={{ 
              background: idleStats.total_idle > 0 ? '#fff7e6' : undefined,
              borderColor: idleStats.total_idle > 0 ? '#ffa940' : undefined
            }}
          >
            <Statistic 
              title={
                <Space>
                  闲置资产
                  {idleStats.total_idle > 0 && <WarningOutlined style={{ color: '#fa8c16' }} />}
                </Space>
              }
              value={idleStats.total_idle} 
              valueStyle={{ color: idleStats.total_idle > 0 ? '#fa8c16' : '#999' }}
              prefix={<InboxOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 闲置资产提示 */}
      {idleStats.total_idle > 0 && !idleOnly && (
        <Alert
          message={`您有 ${idleStats.total_idle} 个闲置资产`}
          description={
            <Space direction="vertical" size="small">
              <div>
                {idleStats.by_type.slice(0, 3).map(item => (
                  <Tag key={item.asset_type_id} color="warning">
                    {item.type_name}: {item.idle_count}个
                  </Tag>
                ))}
              </div>
              <Button 
                type="link" 
                size="small" 
                onClick={() => setIdleOnly(true)}
                icon={<EyeOutlined />}
              >
                查看所有闲置资产
              </Button>
            </Space>
          }
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 主表格 */}
      <Card
        title={
          <Space>
            <span>{idleOnly ? '闲置资产列表' : '资产管理'}</span>
            {idleOnly && <Badge count={idleStats.total_idle} />}
          </Space>
        }
        extra={
          <Space>
            <Switch
              checked={idleOnly}
              onChange={setIdleOnly}
              checkedChildren="仅闲置"
              unCheckedChildren="全部"
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openModal()}
            >
              资产入库
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchAssets();
                fetchIdleStats();
              }}
            >
              刷新
            </Button>
          </Space>
        }
      >
        {/* 搜索和筛选 */}
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Input.Search
                placeholder="搜索资产名称、编号或备注"
                allowClear
                value={searchText}
                onSearch={setSearchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={4}>
              <Select
                placeholder="资产类型"
                allowClear
                style={{ width: '100%' }}
                value={filters.type_id !== null ? filters.type_id : undefined}
                onChange={(value) => setFilters(prev => ({ ...prev, type_id: value }))}
              >
                {assetTypes.map(type => (
                  <Option key={type.id} value={type.id}>
                    {ASSET_TYPE_ICONS[type.type_code]} {type.type_name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <Select
                placeholder="状态"
                allowClear
                style={{ width: '100%' }}
                value={filters.status !== null ? filters.status : undefined}
                onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              >
                {Object.entries(STATUS_MAP).map(([key, config]) => (
                  <Option key={key} value={key}>
                    <Tag color={config.color} icon={config.icon}>{config.text}</Tag>
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <Select
                showSearch
                placeholder="领用员工"
                allowClear
                style={{ width: '100%' }}
                value={filters.employee_id !== null ? filters.employee_id : undefined}
                onChange={(value) => setFilters(prev => ({ ...prev, employee_id: value }))}
                filterOption={(input, option) => {
                  // 安全处理 filterOption，避免对 React 元素调用 toLowerCase
                  const text = option.children;
                  if (typeof text === 'string') {
                    return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                  }
                  // 如果是 React 元素，尝试从 value 属性中获取文本进行匹配
                  const emp = employees.find(e => e.id === option.value);
                  if (emp) {
                    const searchText = `${emp.name} ${emp.employee_no || ''}`.toLowerCase();
                    return searchText.indexOf(input.toLowerCase()) >= 0;
                  }
                  return false;
                }}
              >
                {employees.map(emp => (
                  <Option key={emp.id} value={emp.id}>
                    <UserOutlined style={{ marginRight: 4 }} />
                    {emp.name} {emp.employee_no ? `(${emp.employee_no})` : ''}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={2}>
              <Button
                type="default"
                icon={<ReloadOutlined />}
                onClick={() => {
                  setFilters({
                    type_id: undefined,
                    status: undefined,
                    employee_id: undefined
                  });
                  setSearchText('');
                }}
                title="重置筛选条件"
                style={{ width: '100%' }}
              >
                重置
              </Button>
            </Col>
          </Row>
        </div>

        {/* 资产表格 */}
        <Table
          columns={columns}
          dataSource={assets}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条${idleOnly ? ' 闲置资产' : ''}`
          }}
          onChange={(pagination) => setPagination(pagination)}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 快速入库模态框 - 只要求最基础信息 */}
      <Modal
        title="资产入库"
        open={modalVisible}
        onCancel={closeModal}
        onOk={() => form.submit()}
        width={500}
        destroyOnClose
      >
        <Alert
          message="快速入库"
          description="只需填写基础信息即可快速入库，后续可在详情页面完善其他信息"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ status: 'inactive' }}
        >
          <Form.Item
            name="asset_type_id"
            label="资产类型"
            rules={[{ required: true, message: '请选择资产类型' }]}
          >
            <Select 
              placeholder="请选择资产类型"
              onChange={(value) => setSelectedAssetTypeId(value)}
            >
              {assetTypes.map(type => (
                <Option key={type.id} value={type.id}>
                  {ASSET_TYPE_ICONS[type.type_code]} {type.type_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="name"
            label="资产名称"
            rules={[{ required: true, message: '请输入资产名称' }]}
          >
            <Input placeholder="请输入资产名称" />
          </Form.Item>

          <Form.Item
            name="asset_no"
            label="资产编号"
            tooltip="留空自动生成"
          >
            <Input placeholder="留空自动生成" />
          </Form.Item>

          <Form.Item name="location" label="位置">
            <Input placeholder="请输入位置" />
          </Form.Item>

          <Form.Item name="notes" label="备注">
            <TextArea rows={2} placeholder="请输入备注信息" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 完善信息模态框 - 包含所有详细字段 */}
      <Modal
        title={
          <Space>
            <EditOutlined />
            <span>完善资产信息 - {completingAsset?.name}</span>
            <Tag color="blue">{completingAsset?.asset_no}</Tag>
          </Space>
        }
        open={completeInfoModalVisible}
        onCancel={() => {
          setCompleteInfoModalVisible(false);
          setCompletingAsset(null);
          setAssetImageFiles([]);
          setSelectedAssetTypeId(null);
          completeForm.resetFields();
        }}
        onOk={() => completeForm.submit()}
        width={900}
        destroyOnClose
      >
        <Alert
          message="完善资产详细信息"
          description="填写资产的详细信息、上传图片和自定义字段，完善后才能进行资产领用"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Form
          form={completeForm}
          layout="vertical"
          onFinish={handleCompleteInfoSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="purchase_date" label="购买日期">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="purchase_price" label="购买价格">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="请输入购买价格"
                  formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/¥\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="supplier" label="供应商">
                <Input placeholder="请输入供应商" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="warranty_period" label="保修期(月)">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="请输入保修期"
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="warranty_expires" label="保修到期日期">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="状态">
                <Select>
                  {Object.entries(STATUS_MAP).map(([key, config]) => (
                    <Option key={key} value={key}>
                      <Tag color={config.color} icon={config.icon}>{config.text}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="资产图片"
            tooltip="支持多张图片上传"
          >
            <AttachmentUpload
              ref={assetImageUploadRef}
              value={assetImageFiles}
              compact
              showQrButton={true}
              hideList={false}
              attachToLead={false}
              onChange={(files) => {
                setAssetImageFiles(files);
                if (files && files.length > 0) {
                  const imageUrls = files.map(file => 
                    file.path || file.url || file.fileData?.path || ''
                  ).filter(url => url);
                  completeForm.setFieldsValue({ image_url: JSON.stringify(imageUrls) });
                } else {
                  completeForm.setFieldsValue({ image_url: undefined });
                }
              }}
            />
          </Form.Item>

          <Form.Item name="image_url" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="notes" label="备注">
            <TextArea rows={3} placeholder="请输入备注信息" />
          </Form.Item>

          {/* 动态字段（根据资产类型显示） */}
          {selectedAssetTypeId && (
            <AssetDynamicForm
              ref={dynamicFormRef}
              assetTypeId={selectedAssetTypeId}
              form={completeForm}
              mode="edit"
              initialValues={completingAsset || {}}
            />
          )}
        </Form>
      </Modal>

      {/* 资产关联模态框 */}
      <Modal
        title={
          <Space>
            <LinkOutlined />
            <span>{selectedAsset?.name} - 资产关联</span>
          </Space>
        }
        open={relationModalVisible}
        onCancel={() => setRelationModalVisible(false)}
        onOk={() => relationForm.submit()}
        width={600}
      >
        <Alert
          message="资产物理关系"
          description="例如：SIM卡安装在手机上、配件属于主设备等"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form
          form={relationForm}
          layout="vertical"
          onFinish={handleCreateRelation}
        >
          <Form.Item
            name="relation_name"
            label="关联类型"
            rules={[{ required: true, message: '请选择关联类型' }]}
          >
            <Select placeholder="请选择关联类型">
              {Object.entries(RELATION_TYPES).map(([key, config]) => (
                <Option key={key} value={key}>
                  <Tag color={config.color}>{config.text}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="target_id"
            label="目标资产"
            rules={[{ required: true, message: '请选择目标资产' }]}
          >
            <Select 
              placeholder="请选择资产"
              showSearch
              filterOption={(input, option) => {
                // 安全处理 filterOption，避免对 React 元素调用 toLowerCase
                const text = option.children;
                if (typeof text === 'string') {
                  return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }
                // 如果是 React 元素，尝试从 value 属性中获取文本进行匹配
                const asset = assets.find(a => a.id === option.value);
                if (asset) {
                  const searchText = `${asset.name} ${asset.asset_no || ''}`.toLowerCase();
                  return searchText.indexOf(input.toLowerCase()) >= 0;
                }
                return false;
              }}
            >
              {allAssets
                .filter(a => a.id !== selectedAsset?.id)
                .map(asset => (
                  <Option key={asset.id} value={asset.id}>
                    {ASSET_TYPE_ICONS[asset.type_code]} {asset.name} ({asset.asset_no})
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item name="notes" label="备注">
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 资产领用模态框 */}
      <Modal
        title={
          <Space>
            <UserOutlined />
            <span>{assigningAsset?.name} - 资产领用</span>
          </Space>
        }
        open={assignmentModalVisible}
        onCancel={() => {
          setAssignmentModalVisible(false);
          setAssigningAsset(null);
          assignmentForm.resetFields();
        }}
        onOk={() => assignmentForm.submit()}
        width={600}
      >
        <Alert
          message="资产领用"
          description={assignmentChildren.length > 0 ? 
            `将领用核心资产及其 ${assignmentChildren.length} 个关联资产` : 
            "将资产分配给员工使用"}
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {/* 显示将要领用的资产清单 */}
        {assignmentChildren.length > 0 && (
          <Card size="small" title="将同时领用以下资产" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Tag color="blue">核心资产</Tag>
                <strong>{assigningAsset?.name}</strong>
                <span style={{ fontSize: 12, color: '#999', marginLeft: 8 }}>
                  ({assigningAsset?.asset_no})
                </span>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              {assignmentChildren.map((child, index) => (
                <div key={index}>
                  <Tag color="green">关联资产</Tag>
                  <span>{child.name}</span>
                  <span style={{ fontSize: 12, color: '#999', marginLeft: 8 }}>
                    ({child.asset_no}) - {RELATION_TYPES[child.relation_type]?.text || child.relation_type}
                  </span>
                </div>
              ))}
            </Space>
          </Card>
        )}

        <Form
          form={assignmentForm}
          layout="vertical"
          onFinish={handleAssignment}
        >
          <Form.Item
            name="employee_id"
            label="领用人"
            rules={[{ required: true, message: '请选择领用人' }]}
          >
            <Select 
              placeholder="请选择员工"
              showSearch
              filterOption={(input, option) => {
                // 安全处理 filterOption，避免对 React 元素调用 toLowerCase
                const text = option.children;
                if (typeof text === 'string') {
                  return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }
                // 如果是 React 元素，尝试从 value 属性中获取文本进行匹配
                const emp = employees.find(e => e.id === option.value);
                if (emp) {
                  const searchText = `${emp.name} ${emp.department_name || ''}`.toLowerCase();
                  return searchText.indexOf(input.toLowerCase()) >= 0;
                }
                return false;
              }}
            >
              {employees.map(emp => (
                <Option key={emp.id} value={emp.id}>
                  <UserOutlined /> {emp.name} - {emp.department_name || '未分配部门'}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="assignment_date"
            label="领用日期"
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="notes" label="备注">
            <TextArea rows={2} placeholder="请输入备注（如：领用原因、用途等）" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情抽屉 */}
      <Drawer
        title={
          <Space>
            <span>资产详情</span>
            {(selectedAsset?.is_idle === 1 || selectedAsset?.is_idle === '1' || selectedAsset?.is_idle === true) && <Badge status="warning" text="闲置中" />}
          </Space>
        }
        placement="right"
        width={800}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        extra={
          <Space>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => {
                setDrawerVisible(false);
                openCompleteInfoModal(selectedAsset);
              }}
            >
              完善信息
            </Button>
          </Space>
        }
      >
        {selectedAsset && (
          <Tabs defaultActiveKey="basic">
            <TabPane tab="基础信息" key="basic">
              {/* 图片展示 */}
              {selectedAsset.image_url && (() => {
                try {
                  const imageUrls = typeof selectedAsset.image_url === 'string' && selectedAsset.image_url.startsWith('[')
                    ? JSON.parse(selectedAsset.image_url)
                    : [selectedAsset.image_url];
                  
                  return (
                    <div style={{ marginBottom: 16 }}>
                      <Divider orientation="left">资产图片</Divider>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
                        gap: 12 
                      }}>
                        {imageUrls.map((url, index) => (
                          <div
                            key={index}
                            style={{
                              position: 'relative',
                              cursor: 'pointer',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              transition: 'all 0.3s'
                            }}
                            onClick={() => openImageViewer(imageUrls, index)}
                          >
                            <img
                              src={url}
                              alt={`图片${index + 1}`}
                              style={{
                                width: '100%',
                                height: '150px',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                } catch (error) {
                  return null;
                }
              })()}

              <Descriptions column={2} bordered>
                <Descriptions.Item label="资产编号">{selectedAsset.asset_no}</Descriptions.Item>
                <Descriptions.Item label="资产名称">{selectedAsset.name}</Descriptions.Item>
                <Descriptions.Item label="资产类型">
                  <Tag color="blue">{selectedAsset.type_name}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="状态">
                  {(() => {
                    const config = STATUS_MAP[selectedAsset.status] || { text: selectedAsset.status, color: 'default' };
                    return (
                      <>
                        <Tag color={config.color} icon={config.icon}>
                          {config.text}
                        </Tag>
                        {(selectedAsset.is_idle === 1 || selectedAsset.is_idle === '1' || selectedAsset.is_idle === true) && (
                          <Tag color="warning" style={{ marginLeft: 8 }}>
                            <WarningOutlined /> 闲置
                          </Tag>
                        )}
                      </>
                    );
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label="位置">{selectedAsset.location || '-'}</Descriptions.Item>
                <Descriptions.Item label="购买日期">
                  {selectedAsset.purchase_date ? dayjs(selectedAsset.purchase_date).format('YYYY-MM-DD') : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="购买价格">
                  {selectedAsset.purchase_price ? `¥${selectedAsset.purchase_price}` : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="供应商">{selectedAsset.supplier || '-'}</Descriptions.Item>
                <Descriptions.Item label="保修期">
                  {selectedAsset.warranty_period ? `${selectedAsset.warranty_period}个月` : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="保修到期">
                  {selectedAsset.warranty_expires ? dayjs(selectedAsset.warranty_expires).format('YYYY-MM-DD') : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="备注" span={2}>
                  {selectedAsset.notes || '-'}
                </Descriptions.Item>
              </Descriptions>

              {/* 动态字段显示 */}
              {selectedAsset._field_values && selectedAsset._field_values.length > 0 && (
                <>
                  <Divider orientation="left">自定义信息</Divider>
                  <Descriptions column={2} bordered>
                    {selectedAsset._field_values.map(field => {
                      // 将字段值转换为显示标签
                      const getFieldDisplayValue = (fieldValue, fieldType, fieldOptions) => {
                        if (!fieldValue) return '-';
                        
                        if (fieldType === 'select' && fieldOptions) {
                          try {
                            let parsedOptions = fieldOptions;
                            if (typeof fieldOptions === 'string') {
                              parsedOptions = JSON.parse(fieldOptions);
                            }
                            
                            if (typeof parsedOptions === 'object' && parsedOptions.options) {
                              const option = parsedOptions.options.find(opt => 
                                (opt.value || opt) === fieldValue
                              );
                              return option ? (option.label || option.value || option) : fieldValue;
                            } else if (Array.isArray(parsedOptions)) {
                              return parsedOptions.includes(fieldValue) ? fieldValue : fieldValue;
                            }
                          } catch (error) {
                            console.error('解析字段选项失败:', error);
                          }
                        } else if (fieldType === 'boolean') {
                          return fieldValue === '1' || fieldValue === 'true' ? '是' : '否';
                        } else if (fieldType === 'date') {
                          return fieldValue ? dayjs(fieldValue).format('YYYY-MM-DD') : '-';
                        } else if (fieldType === 'asset_reference') {
                          // 资产关联类型，显示关联的资产信息
                          if (!fieldValue) return '-';
                          
                          // 优先使用后端返回的关联资产信息
                          if (field.referenced_asset_info) {
                            const assetInfo = field.referenced_asset_info;
                            const statusConfig = STATUS_MAP[assetInfo.status] || { text: assetInfo.status, color: 'default' };
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <Tag color="blue" style={{ fontWeight: 500 }}>{assetInfo.name}</Tag>
                                  {assetInfo.asset_no && (
                                    <span style={{ fontSize: '12px', color: '#666', background: '#f5f5f5', padding: '2px 6px', borderRadius: '4px' }}>
                                      {assetInfo.asset_no}
                                    </span>
                                  )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                                  <span style={{ color: '#999' }}>状态:</span>
                                  <Tag 
                                    color={statusConfig.color} 
                                    size="small"
                                    icon={statusConfig.icon}
                                  >
                                    {statusConfig.text}
                                  </Tag>
                                  {assetInfo.type_name && (
                                    <>
                                      <span style={{ color: '#999' }}>类型:</span>
                                      <Tag color="cyan" size="small">{assetInfo.type_name}</Tag>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          
                          // 尝试从当前资产列表中查找关联的资产信息
                          const referencedAsset = assets.find(asset => asset.id == fieldValue);
                          if (referencedAsset) {
                            const statusConfig = STATUS_MAP[referencedAsset.status] || { text: referencedAsset.status, color: 'default' };
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <Tag color="blue" style={{ fontWeight: 500 }}>{referencedAsset.name}</Tag>
                                  {referencedAsset.asset_no && (
                                    <span style={{ fontSize: '12px', color: '#666', background: '#f5f5f5', padding: '2px 6px', borderRadius: '4px' }}>
                                      {referencedAsset.asset_no}
                                    </span>
                                  )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                                  <span style={{ color: '#999' }}>状态:</span>
                                  <Tag 
                                    color={statusConfig.color} 
                                    size="small"
                                    icon={statusConfig.icon}
                                  >
                                    {statusConfig.text}
                                  </Tag>
                                  {referencedAsset.type_name && (
                                    <>
                                      <span style={{ color: '#999' }}>类型:</span>
                                      <Tag color="cyan" size="small">{referencedAsset.type_name}</Tag>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          
                          // 如果找不到资产信息，显示ID
                          return `资产ID: ${fieldValue}`;
                        }
                        
                        return fieldValue;
                      };
                      
                      return (
                        <Descriptions.Item key={field.field_config_id} label={field.field_name}>
                          {getFieldDisplayValue(field.field_value, field.field_type, field.field_options)}
                        </Descriptions.Item>
                      );
                    })}
                  </Descriptions>
                </>
              )}
            </TabPane>

            <TabPane 
              tab={
                <Space>
                  资产关联
                  <Badge count={assetRelations.outgoing.filter(r => r.status === 'active').length + assetRelations.incoming.filter(r => r.status === 'active').length} />
                </Space>
              } 
              key="relations"
            >
              {/* Tab操作按钮 */}
              <div style={{ marginBottom: 16, textAlign: 'right' }}>
                <Button
                  type="primary"
                  size="small"
                  icon={<LinkOutlined />}
                  onClick={() => {
                    setDrawerVisible(false);
                    openRelationModal(selectedAsset);
                  }}
                  disabled={selectedAsset?.has_parent_relation === 1 || selectedAsset?.has_parent_relation === '1'}
                >
                  添加关联
                </Button>
              </div>

              {/* 活跃关系 */}
              {(assetRelations.outgoing.filter(r => r.status === 'active').length > 0 || 
                assetRelations.incoming.filter(r => r.status === 'active').length > 0) && (
                <>
                  <Divider orientation="left">当前关系</Divider>
                  <Timeline>
                    {/* 该资产关联的其他资产 */}
                    {assetRelations.outgoing.filter(r => r.status === 'active').map(rel => (
                      <Timeline.Item
                        key={rel.id}
                        color={RELATION_TYPES[rel.relation_name]?.color || 'blue'}
                        dot={<LinkOutlined />}
                      >
                        <Space size="middle" style={{ width: '100%', justifyContent: 'space-between' }}>
                          <Space>
                            <Tag color={RELATION_TYPES[rel.relation_name]?.color}>
                              {RELATION_TYPES[rel.relation_name]?.text || rel.relation_name}
                            </Tag>
                            <Button 
                              type="link" 
                              size="small"
                              onClick={() => {
                                const targetAsset = assets.find(a => a.id === rel.child_asset_id);
                                if (targetAsset) {
                                  viewNestedAssetDetails(targetAsset);
                                }
                              }}
                            >
                              <strong>{rel.target_name}</strong>
                            </Button>
                            <span style={{ fontSize: 12, color: '#999' }}>
                              {dayjs(rel.start_date).format('YYYY-MM-DD HH:mm')}
                            </span>
                            {rel.notes && <Tooltip title={rel.notes}><Tag>备注</Tag></Tooltip>}
                          </Space>
                          <Popconfirm
                            title="确认解除此关系？"
                            onConfirm={() => handleDeleteRelation(rel.id, '手动解除')}
                          >
                            <Button size="small" danger type="link">解除</Button>
                          </Popconfirm>
                        </Space>
                      </Timeline.Item>
                    ))}
                    
                    {/* 其他资产关联到该资产 */}
                    {assetRelations.incoming.filter(r => r.status === 'active').map(rel => (
                      <Timeline.Item
                        key={rel.id}
                        color={RELATION_TYPES[rel.relation_name]?.color || 'gray'}
                        dot={<LinkOutlined />}
                      >
                        <Space size="middle" style={{ width: '100%', justifyContent: 'space-between' }}>
                          <Space>
                            <span>该资产</span>
                            <Tag color={RELATION_TYPES[rel.relation_name]?.color}>
                              {RELATION_TYPES[rel.relation_name]?.text || rel.relation_name}
                            </Tag>
                            <Button 
                              type="link" 
                              size="small"
                              onClick={() => {
                                const sourceAsset = assets.find(a => a.id === rel.parent_asset_id);
                                if (sourceAsset) {
                                  viewNestedAssetDetails(sourceAsset);
                                }
                              }}
                            >
                              <strong>{rel.source_name}</strong>
                            </Button>
                            <span style={{ fontSize: 12, color: '#999' }}>
                              {dayjs(rel.start_date).format('YYYY-MM-DD HH:mm')}
                            </span>
                            {rel.notes && <Tooltip title={rel.notes}><Tag>备注</Tag></Tooltip>}
                          </Space>
                          <Popconfirm
                            title="确认解除此关系？"
                            onConfirm={() => handleDeleteRelation(rel.id, '手动解除')}
                          >
                            <Button size="small" danger type="link">解除</Button>
                          </Popconfirm>
                        </Space>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </>
              )}

              {/* 历史关系（已失效） */}
              {(assetRelations.outgoing.filter(r => r.status === 'inactive').length > 0 || 
                assetRelations.incoming.filter(r => r.status === 'inactive').length > 0) && (
                <>
                  <Divider orientation="left">历史关系</Divider>
                  <Timeline
                    items={assetRelations.outgoing.filter(r => r.status === 'inactive').map(rel => ({
                      key: rel.id,
                      color: "gray",
                      dot: <SwapOutlined />,
                      children: (
                        <div style={{ opacity: 0.7 }}>
                          <Space size="small" wrap>
                            <Tag color="default">
                              {RELATION_TYPES[rel.relation_name]?.text || rel.relation_name}
                            </Tag>
                            <Button 
                              type="link" 
                              size="small"
                              onClick={() => {
                                // 查找目标资产并打开详情
                                const targetAsset = assets.find(a => a.id === rel.child_asset_id);
                                if (targetAsset) {
                                  viewNestedAssetDetails(targetAsset);
                                }
                              }}
                            >
                              {rel.target_name}
                            </Button>
                            <Tag color="default">已解除</Tag>
                          </Space>
                          <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                            <Space size="middle" split="|">
                              <span>绑定：{dayjs(rel.start_date).format('YYYY-MM-DD HH:mm')}</span>
                              {rel.end_date && <span>解除：{dayjs(rel.end_date).format('YYYY-MM-DD HH:mm')}</span>}
                              {rel.created_by_name && <span>操作人：{rel.created_by_name}</span>}
                            </Space>
                          </div>
                        </div>
                      )
                    }))}
                  />
                  <Timeline
                    items={assetRelations.incoming.filter(r => r.status === 'inactive').map(rel => ({
                      key: rel.id,
                      color: "gray",
                      dot: <SwapOutlined />,
                      children: (
                        <div style={{ opacity: 0.7 }}>
                          <Space size="small" wrap>
                            <span>该资产</span>
                            <Tag color="default">
                              {RELATION_TYPES[rel.relation_name]?.text || rel.relation_name}
                            </Tag>
                            <Button 
                              type="link" 
                              size="small"
                              onClick={() => {
                                const sourceAsset = assets.find(a => a.id === rel.parent_asset_id);
                                if (sourceAsset) {
                                  viewNestedAssetDetails(sourceAsset);
                                }
                              }}
                            >
                              {rel.source_name}
                            </Button>
                            <Tag color="default">已解除</Tag>
                          </Space>
                          <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                            <Space size="middle" split="|">
                              <span>绑定：{dayjs(rel.start_date).format('YYYY-MM-DD HH:mm')}</span>
                              {rel.end_date && <span>解除：{dayjs(rel.end_date).format('YYYY-MM-DD HH:mm')}</span>}
                              {rel.created_by_name && <span>操作人：{rel.created_by_name}</span>}
                            </Space>
                          </div>
                        </div>
                      )
                    }))}
                  />
                </>
              )}

              {assetRelations.outgoing.length === 0 && assetRelations.incoming.length === 0 && (
                <Empty description="暂无关系" />
              )}
            </TabPane>

            {/* 领用信息Tab */}
            <TabPane 
              tab={
                <Space>
                  领用信息
                  {selectedAsset?.current_holder && <Badge status="success" />}
                </Space>
              } 
              key="assignment"
            >
              {/* Tab操作按钮 */}
              <div style={{ marginBottom: 16, textAlign: 'right' }}>
                <Space>
                  {selectedAsset?.current_holder && (
                    <Popconfirm
                      title="确认归还此资产？"
                      description="归还后资产将变为闲置状态"
                      onConfirm={() => handleReturnAsset(selectedAsset)}
                      okText="确认归还"
                      cancelText="取消"
                    >
                      <Button
                        size="small"
                        icon={<SwapOutlined />}
                      >
                        归还资产
                      </Button>
                    </Popconfirm>
                  )}
                  <Button
                    type="primary"
                    size="small"
                    icon={<UserOutlined />}
                    onClick={async () => {
                      setDrawerVisible(false);
                      // 使用完整的验证流程，支持子资产联动
                      await openAssignmentModal(selectedAsset);
                    }}
                    disabled={selectedAsset?.current_holder}
                  >
                    {selectedAsset?.current_holder ? '转移领用' : '资产领用'}
                  </Button>
                </Space>
              </div>

              {/* 当前领用信息 */}
              {selectedAsset?.current_holder ? (
                <>
                  <Divider orientation="left">当前领用</Divider>
                  <Card size="small" style={{ marginBottom: 16, background: '#f6ffed', borderColor: '#b7eb8f' }}>
                    <Descriptions column={2} size="small">
                      <Descriptions.Item label="领用人">
                        <Space>
                          <UserOutlined />
                          <strong>{selectedAsset.holder_name || '未知'}</strong>
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="状态">
                        <Tag color="green">使用中</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="领用时间" span={2}>
                        {selectedAsset.updated_at ? dayjs(selectedAsset.updated_at).format('YYYY-MM-DD HH:mm') : '-'}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </>
              ) : (
                <Alert
                  message="该资产暂未领用"
                  description="点击上方按钮可以将资产分配给员工使用"
                  type="info"
                  showIcon
                />
              )}

              {/* 领用历史 */}
              {assignmentHistory && assignmentHistory.length > 0 && (
                <>
                  <Divider orientation="left">领用历史</Divider>
                  <Timeline>
                    {assignmentHistory.map((history) => {
                      const isAssigned = history.action_type === 'assigned';
                      const isReturned = history.action_type === 'returned';
                      
                      let newValueObj = {};
                      let oldValueObj = {};
                      
                      try {
                        if (history.new_value) {
                          newValueObj = typeof history.new_value === 'string' 
                            ? JSON.parse(history.new_value) 
                            : history.new_value;
                        }
                        if (history.old_value) {
                          oldValueObj = typeof history.old_value === 'string' 
                            ? JSON.parse(history.old_value) 
                            : history.old_value;
                        }
                      } catch (e) {
                        console.error('解析历史数据失败:', e);
                      }

                      return (
                        <Timeline.Item
                          key={history.id}
                          color={isAssigned ? 'green' : 'gray'}
                          dot={isAssigned ? <UserOutlined /> : <SwapOutlined />}
                        >
                          <div style={{ opacity: isReturned ? 0.7 : 1 }}>
                            <Space size="small" wrap>
                              {isAssigned ? (
                                <>
                                  <Tag color="green">领用</Tag>
                                  <UserOutlined />
                                  <strong>{newValueObj.holder_name || '未知'}</strong>
                                </>
                              ) : (
                                <>
                                  <Tag color="default">归还</Tag>
                                  <UserOutlined />
                                  <strong>{oldValueObj.holder_name || '未知'}</strong>
                                  <span style={{ fontSize: 12, color: '#999' }}>
                                    (归还人：{history.operator_name || '未知'})
                                  </span>
                                </>
                              )}
                            </Space>
                            <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                              <Space size="middle" split="|">
                                <span>时间：{dayjs(history.created_at).format('YYYY-MM-DD HH:mm')}</span>
                                <span>操作人：{history.operator_name || '未知'}</span>
                                {history.description && <span>{history.description}</span>}
                              </Space>
                            </div>
                          </div>
                        </Timeline.Item>
                      );
                    })}
                  </Timeline>
                </>
              )}
            </TabPane>
          </Tabs>
        )}
      </Drawer>

      {/* 嵌套资产详情抽屉（第二层） */}
      <Drawer
        title={
          <Space>
            <DesktopOutlined />
            <span>{nestedSelectedAsset?.name} - 关联资产详情</span>
            {(nestedSelectedAsset?.is_idle === 1 || nestedSelectedAsset?.is_idle === '1' || nestedSelectedAsset?.is_idle === true) && <Badge status="warning" text="闲置中" />}
          </Space>
        }
        placement="right"
        width={800}
        open={nestedDrawerVisible}
        onClose={() => {
          setNestedDrawerVisible(false);
          setNestedSelectedAsset(null);
        }}
        style={{ position: 'absolute' }} // 使嵌套抽屉覆盖在第一层上
        maskClosable={false}
      >
        {nestedSelectedAsset && (
          <Tabs defaultActiveKey="basic">
            <TabPane tab="基础信息" key="basic">
              {/* 图片展示 */}
              {nestedSelectedAsset.image_url && (() => {
                try {
                  const imageUrls = typeof nestedSelectedAsset.image_url === 'string' && nestedSelectedAsset.image_url.startsWith('[')
                    ? JSON.parse(nestedSelectedAsset.image_url)
                    : [nestedSelectedAsset.image_url];
                  
                  return (
                    <div style={{ marginBottom: 16 }}>
                      <Divider orientation="left">资产图片</Divider>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
                        gap: 12 
                      }}>
                        {imageUrls.map((url, index) => (
                          <div
                            key={index}
                            style={{
                              position: 'relative',
                              cursor: 'pointer',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              transition: 'all 0.3s'
                            }}
                            onClick={() => openImageViewer(imageUrls, index)}
                          >
                            <img
                              src={url}
                              alt={`图片${index + 1}`}
                              style={{
                                width: '100%',
                                height: '150px',
                                objectFit: 'cover',
                                display: 'block'
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                } catch (error) {
                  return null;
                }
              })()}

              <Descriptions column={2} bordered>
                <Descriptions.Item label="资产编号">{nestedSelectedAsset.asset_no}</Descriptions.Item>
                <Descriptions.Item label="资产名称">{nestedSelectedAsset.name}</Descriptions.Item>
                <Descriptions.Item label="资产类型">
                  <Tag color="blue">{nestedSelectedAsset.type_name}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="状态">
                  {(() => {
                    let displayStatus = nestedSelectedAsset.status;
                    if (nestedSelectedAsset.current_holder) {
                      displayStatus = 'active';
                    } else if (!nestedSelectedAsset.current_holder && nestedSelectedAsset.status === 'active') {
                      displayStatus = 'inactive';
                    }
                    const config = STATUS_MAP[displayStatus] || { text: displayStatus, color: 'default' };
                    return (
                      <Tag color={config.color} icon={config.icon}>
                        {config.text}
                      </Tag>
                    );
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label="位置">{nestedSelectedAsset.location || '-'}</Descriptions.Item>
                <Descriptions.Item label="当前持有人">
                  {nestedSelectedAsset.holder_name ? (
                    <Tag color="blue" icon={<UserOutlined />}>
                      {nestedSelectedAsset.holder_name}
                    </Tag>
                  ) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="购买日期">
                  {nestedSelectedAsset.purchase_date ? dayjs(nestedSelectedAsset.purchase_date).format('YYYY-MM-DD') : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="购买价格">
                  {nestedSelectedAsset.purchase_price ? `¥${nestedSelectedAsset.purchase_price}` : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="供应商">{nestedSelectedAsset.supplier || '-'}</Descriptions.Item>
                <Descriptions.Item label="保修期">
                  {nestedSelectedAsset.warranty_period ? `${nestedSelectedAsset.warranty_period}个月` : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="保修到期日">
                  {nestedSelectedAsset.warranty_expires ? dayjs(nestedSelectedAsset.warranty_expires).format('YYYY-MM-DD') : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="创建时间">
                  {dayjs(nestedSelectedAsset.created_at).format('YYYY-MM-DD HH:mm')}
                </Descriptions.Item>
                <Descriptions.Item label="备注" span={2}>{nestedSelectedAsset.notes || '-'}</Descriptions.Item>
              </Descriptions>

              {/* 动态字段显示 */}
              {nestedSelectedAsset._field_values && nestedSelectedAsset._field_values.length > 0 && (
                <>
                  <Divider orientation="left">自定义信息</Divider>
                  <Descriptions column={2} bordered>
                    {nestedSelectedAsset._field_values.map(field => {
                      // 将字段值转换为显示标签
                      const getFieldDisplayValue = (fieldValue, fieldType, fieldOptions) => {
                        if (!fieldValue) return '-';
                        
                        if (fieldType === 'text' || fieldType === 'textarea') {
                          return fieldValue;
                        } else if (fieldType === 'number') {
                          return fieldValue;
                        } else if (fieldType === 'select' || fieldType === 'radio') {
                          // 如果有选项配置，尝试匹配显示文本
                          if (fieldOptions && fieldOptions.options) {
                            const option = fieldOptions.options.find(opt => opt.value === fieldValue);
                            if (option) {
                              return option.label || option.text || fieldValue;
                            }
                          }
                          return fieldValue;
                        } else if (fieldType === 'checkbox') {
                          return Array.isArray(fieldValue) ? fieldValue.join(', ') : fieldValue;
                        } else if (fieldType === 'date') {
                          return fieldValue ? dayjs(fieldValue).format('YYYY-MM-DD') : '-';
                        } else if (fieldType === 'datetime') {
                          return fieldValue ? dayjs(fieldValue).format('YYYY-MM-DD HH:mm:ss') : '-';
                        } else if (fieldType === 'asset_reference') {
                          // 资产关联类型，显示关联的资产信息
                          if (!fieldValue) return '-';
                          
                          // 优先使用后端返回的关联资产信息
                          if (field.referenced_asset_info) {
                            const assetInfo = field.referenced_asset_info;
                            const statusColor = assetInfo.status === 'active' ? 'green' : 'red';
                            const statusIcon = assetInfo.status === 'active' ? <CheckCircleOutlined /> : <CloseCircleOutlined />;
                            const statusText = assetInfo.status === 'active' ? '正常' : '停用';
                            
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px', background: '#f5f5f5', borderRadius: '6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                  <Tag color="blue" style={{ fontWeight: 500, fontSize: '12px' }}>{assetInfo.name}</Tag>
                                  {assetInfo.asset_no && (
                                    <Tag color="default" style={{ borderRadius: '12px', fontSize: '11px' }}>{assetInfo.asset_no}</Tag>
                                  )}
                                  <Tag icon={statusIcon} color={statusColor} size="small" style={{ fontSize: '11px' }}>
                                    {statusText}
                                  </Tag>
                                  {assetInfo.type_name && (
                                    <Tag color="cyan" size="small" style={{ fontSize: '11px' }}>{assetInfo.type_name}</Tag>
                                  )}
                                </div>
                              </div>
                            );
                          }

                          // 尝试从当前资产列表中查找关联的资产信息 (备选方案)
                          const referencedAsset = assets.find(asset => asset.id == fieldValue);
                          if (referencedAsset) {
                            const statusColor = referencedAsset.status === 'active' ? 'green' : 'red';
                            const statusIcon = referencedAsset.status === 'active' ? <CheckCircleOutlined /> : <CloseCircleOutlined />;
                            const statusText = referencedAsset.status === 'active' ? '正常' : '停用';
                            
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px', background: '#f5f5f5', borderRadius: '6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                  <Tag color="blue" style={{ fontWeight: 500, fontSize: '12px' }}>{referencedAsset.name}</Tag>
                                  {referencedAsset.asset_no && (
                                    <Tag color="default" style={{ borderRadius: '12px', fontSize: '11px' }}>{referencedAsset.asset_no}</Tag>
                                  )}
                                  <Tag icon={statusIcon} color={statusColor} size="small" style={{ fontSize: '11px' }}>
                                    {statusText}
                                  </Tag>
                                  {referencedAsset.type_name && (
                                    <Tag color="cyan" size="small" style={{ fontSize: '11px' }}>{referencedAsset.type_name}</Tag>
                                  )}
                                </div>
                              </div>
                            );
                          }

                          // 最终备选方案，只显示ID
                          return `资产ID: ${fieldValue}`;
                        }
                        
                        return fieldValue;
                      };

                      // 安全解析 field_options
                      let fieldOptions = null;
                      if (field.field_options) {
                        try {
                          if (typeof field.field_options === 'object') {
                            fieldOptions = field.field_options;
                          } else {
                            fieldOptions = JSON.parse(field.field_options);
                          }
                        } catch (e) {
                          console.error(`字段 ${field.field_name} (ID: ${field.field_config_id}) 的 field_options 解析失败:`, field.field_options);
                          fieldOptions = null;
                        }
                      }

                      return (
                        <Descriptions.Item 
                          key={field.field_config_id} 
                          label={field.field_name}
                          span={field.field_type === 'textarea' ? 2 : 1}
                        >
                          {getFieldDisplayValue(field.field_value, field.field_type, fieldOptions)}
                        </Descriptions.Item>
                      );
                    })}
                  </Descriptions>
                </>
              )}
            </TabPane>

            <TabPane 
              tab={
                <Space>
                  资产关联
                  <Badge count={nestedAssetRelations.outgoing.filter(r => r.status === 'active').length + nestedAssetRelations.incoming.filter(r => r.status === 'active').length} />
                </Space>
              } 
              key="relations"
            >
              {/* 当前关系 */}
              {(nestedAssetRelations.outgoing.filter(r => r.status === 'active').length > 0 || 
                nestedAssetRelations.incoming.filter(r => r.status === 'active').length > 0) && (
                <>
                  <Divider orientation="left">当前关系</Divider>
                  <Timeline>
                    {/* 该资产关联的其他资产 */}
                    {nestedAssetRelations.outgoing.filter(r => r.status === 'active').map(rel => (
                      <Timeline.Item
                        key={rel.id}
                        color={RELATION_TYPES[rel.relation_name]?.color || 'blue'}
                        dot={<LinkOutlined />}
                      >
                        <Space size="small" wrap>
                          <Tag color={RELATION_TYPES[rel.relation_name]?.color}>
                            {RELATION_TYPES[rel.relation_name]?.text || rel.relation_name}
                          </Tag>
                          <strong>{rel.target_name}</strong>
                          <span style={{ fontSize: 12, color: '#999' }}>
                            {dayjs(rel.start_date).format('YYYY-MM-DD HH:mm')}
                          </span>
                        </Space>
                      </Timeline.Item>
                    ))}
                    
                    {/* 其他资产关联到该资产 */}
                    {nestedAssetRelations.incoming.filter(r => r.status === 'active').map(rel => (
                      <Timeline.Item
                        key={rel.id}
                        color={RELATION_TYPES[rel.relation_name]?.color || 'gray'}
                        dot={<LinkOutlined />}
                      >
                        <Space size="small" wrap>
                          <span>该资产</span>
                          <Tag color={RELATION_TYPES[rel.relation_name]?.color}>
                            {RELATION_TYPES[rel.relation_name]?.text || rel.relation_name}
                          </Tag>
                          <strong>{rel.source_name}</strong>
                          <span style={{ fontSize: 12, color: '#999' }}>
                            {dayjs(rel.start_date).format('YYYY-MM-DD HH:mm')}
                          </span>
                        </Space>
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </>
              )}

              {/* 无关联资产提示 */}
              {nestedAssetRelations.outgoing.filter(r => r.status === 'active').length === 0 && 
               nestedAssetRelations.incoming.filter(r => r.status === 'active').length === 0 && (
                <Empty description="暂无关联资产" />
              )}
            </TabPane>

            <TabPane tab="领用历史" key="history">
              {nestedAssignmentHistory.length > 0 ? (
                <Timeline>
                  {nestedAssignmentHistory.map((record, index) => (
                    <Timeline.Item
                      key={index}
                      color={record.action_type === 'assigned' ? 'green' : 'gray'}
                      dot={record.action_type === 'assigned' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                    >
                      <div>
                        <Space>
                          <Tag color={record.action_type === 'assigned' ? 'green' : 'default'}>
                            {record.action_type === 'assigned' ? '领用' : '归还'}
                          </Tag>
                          <strong>{record.description}</strong>
                        </Space>
                        <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>
                          {dayjs(record.created_at).format('YYYY-MM-DD HH:mm:ss')}
                          {record.operator_name && ` · 操作人：${record.operator_name}`}
                        </div>
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              ) : (
                <Empty description="暂无领用历史" />
              )}
            </TabPane>
          </Tabs>
        )}
      </Drawer>

      {/* 图片预览器 */}
      <ImageViewer
        visible={imageViewerVisible}
        onClose={closeImageViewer}
        imageUrl={imageList[currentImageIndex]}
        imageName={`资产图片-${currentImageIndex + 1}`}
        downloadUrl={imageList[currentImageIndex]}
        images={imageList}
        currentIndex={currentImageIndex}
        onIndexChange={setCurrentImageIndex}
      />
    </div>
  );
};

export default AssetManagementV2;
